import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Dropdown, Card, Divider } from 'pepsico-ds';
import { CircularProgress } from '@mui/material';
import HomeTextComponent from './HomeTextComponent';
import PortfolioHealthIcon from '../../assests/images/portfolio-health-icon.svg';
import {
  CORE_USER,
  GENERAL_USER,
  homePageCycleTableColumns,
  homeScreenQuickLinks,
  timeFrameOptions,
  TPO_ADMIN
} from '../../utils/Constants';
import { fetchActualVsPreviousPeriodData } from '../PortfolioMasterData/services';
import CycleDrawer from './CycleDrawer';
import { fetchCycleTriggerData, fetchPeriodDates, createCycle } from '../CycleTrigger/services';
import { useOkta } from '../../hooks/oktaHook';
import DynamicTable from '../../components/Table/Table';
import { setHistoricalCyclesValue } from '../../redux/General/generalSlice';
import { setPeriodsData } from '../../redux/HomePage/cyclesByBuSlice';
import './HomePage.css';
import { getUrlWithParams } from '../../utils/Utils';

const NewHomePage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [timeFrameValue, setTimeFrameValue] = useState([timeFrameOptions?.[0]]);
  const [kpiData, setKpiData] = useState([]);
  const [showDrawer, setShowDrawer] = useState(false);
  const [loaders, setLoaders] = useState({ kpiDataLoader: false });
  const [cyclesData, setCyclesData] = useState([]);
  const { userDetails } = useOkta();
  const { roles, email } = userDetails;
  const { kpiDataLoader } = loaders;
  const { search } = useLocation();

  const newParams = new URLSearchParams(search);
  const selectedCycleId = Number(newParams.get('cycleId'));
  const selectedCycle = newParams.get('cycleName');
  const selectedBu = newParams.get('bu');

  const cyclesSliceData = useSelector((state) => state?.cycle);
  const { periodsData } = cyclesSliceData;

  useEffect(() => {
    if (selectedBu) {
      setLoaders((prevState) => ({
        ...prevState,
        kpiDataLoader: true
      }));
      let params = {
        trendAnalysisBrekup: 'Revenue',
        levelName: 'Brand',
        buName: selectedBu,
        timeframe: timeFrameValue?.[0]?.displayText
      };
      fetchActualVsPreviousPeriodData(params).then((res) => {
        setLoaders((prevState) => ({
          ...prevState,
          kpiDataLoader: false
        }));
        setKpiData(res);
      });
      fetchCycleTriggerData(selectedBu).then((res) => setCyclesData(res));
      const selectedTimeFrame = timeFrameValue?.[0]?.id;
      fetchPeriodDates(selectedBu, selectedTimeFrame).then((res) =>
        dispatch(setPeriodsData(res?.periodRange))
      );
    }
  }, [timeFrameValue?.[0]?.displayText, selectedBu]);

  const handleCreateCycle = async (data) => {
    try {
      await createCycle(data, email);
      const cycles = await fetchCycleTriggerData(selectedBu);
      setCyclesData(cycles);
      setShowDrawer(false);
    } catch (error) {
      console.error('Create Cycle Failed:', error);
    }
  };

  const getVotingStatusOfCycle = () => {
    let isCyclesCompleted = true;
    if (
      cyclesData?.filter((item) => !item?.isFinalDecisionComplete && !item?.testCycle)?.length > 0
    ) {
      isCyclesCompleted = false;
    }
    return isCyclesCompleted;
  };

  return (
    <div className="home-page-container">
      <HomeTextComponent />
      <Card ellipsis={false} icon="">
        <div className="header-container">
          <div className="title-section">
            <img src={PortfolioHealthIcon} alt="Portfolio-health-icon" />
            <span className="portfolio-header-text">Portfolio Health</span>
          </div>
          <div className="dropdown-section">
            <label className="timeframe-label-text">Timeframe</label>
            <Dropdown
              isClearable={false}
              childList={timeFrameOptions}
              selectedValue={timeFrameValue}
              setSelectedValue={(value) => setTimeFrameValue(value)}
            />
          </div>
        </div>
        <div className="metrics-card-section">
          <Card ellipsis={false} icon="">
            {kpiDataLoader ? (
              <CircularProgress />
            ) : (
              kpiData?.map((item, index) => (
                <>
                  <div className="metrics-section">
                    <span className="kpi-title">{item?.title}</span>
                    <span className="kpi-value">{item?.value}</span>
                    <span className="kpi-difference">
                      <span
                        style={{
                          color:
                            item?.textcolor === 'green'
                              ? '#008553'
                              : item?.textcolor === 'red'
                                ? '#D40020'
                                : ''
                        }}>
                        {item.percentdifference}
                      </span>{' '}
                      <span style={{ fontWeight: '400', color: '#616161', fontSize: '12px' }}>
                        vs Previous{' '}
                        {timeFrameValue?.[0]?.id === timeFrameOptions?.[0]?.id
                          ? '13 Periods'
                          : 'YTD'}
                      </span>
                    </span>
                  </div>
                  {index + 1 !== kpiData?.length && <Divider className="line-seperator" />}
                </>
              ))
            )}
          </Card>
          <div className="portfolio-health-btn">
            <Button
              data-testid="portfolio-health-btn"
              variant="primary"
              onClick={() =>
                navigate(
                  getUrlWithParams(
                    '/portfolio/portfolio-health',
                    selectedBu,
                    selectedCycle,
                    selectedCycleId
                  )
                )
              }>
              Go to Portfolio Health
            </Button>
          </div>
        </div>
      </Card>
      <h3>Current Cycles</h3>
      <div className="cycles-card-section">
        <Card ellipsis={false} icon="">
          {cyclesData?.length > 0 && (
            <div className="cycle-table">
              {roles?.includes(GENERAL_USER) &&
              cyclesData?.filter(
                (item) => item?.activeStatus === true || item?.isCycleOutOfVoteTracking === false
              )?.length === 0 ? (
                <div className="cycle-empty-text">There is No active cycle to vote</div>
              ) : (
                <DynamicTable
                  data-testid="logs-table"
                  originalColumns={homePageCycleTableColumns}
                  tableName=""
                  originalData={
                    roles?.includes(GENERAL_USER)
                      ? cyclesData?.filter(
                          (item) =>
                            item?.activeStatus === true || item?.isCycleOutOfVoteTracking === false
                        )
                      : cyclesData
                  }
                  isCollapseButton={false}
                  isSearch={false}
                  defaultRowsPerPage={5}
                />
              )}
            </div>
          )}
          <div
            className="create-cycle-btn"
            style={{
              justifyContent: cyclesData?.length > 0 ? 'end' : 'center',
              marginTop: cyclesData?.length === 0 ? '5px' : '-20px'
            }}>
            <Button
              variant="primary"
              data-testid="create-cycle-button"
              disabled={!roles?.some((role) => [CORE_USER, TPO_ADMIN].includes(role))}
              onClick={() => setShowDrawer(true)}>
              Set up new cycle
            </Button>
          </div>
        </Card>
      </div>
      <div className="quick-links-section">
        <Card ellipsis={false} icon="">
          <h3>Quick Links</h3>
          <div className="inner-card-container">
            {homeScreenQuickLinks?.map((item) => (
              <div
                key={item.name}
                className="links-cards-container"
                onClick={() => {
                  dispatch(setHistoricalCyclesValue('globalCycleTriggerQueue'));
                  navigate(
                    getUrlWithParams(item?.link, selectedBu, selectedCycle, selectedCycleId)
                  );
                }}>
                <Card ellipsis={false} icon="">
                  <div className="card-names-container">
                    <img src={item?.icon} alt={item.name} />
                    <div>
                      <div className="title-name">{item.name}</div>
                      <div className="title-description">{item.description}</div>
                    </div>
                  </div>
                </Card>
              </div>
            ))}
          </div>
        </Card>
      </div>
      <CycleDrawer
        showDrawer={showDrawer}
        handleClose={() => setShowDrawer(false)}
        selectedBu={selectedBu}
        handleCreateCycle={(data) => handleCreateCycle(data)}
        periodsData={periodsData}
        isCycleVotingCompleted={getVotingStatusOfCycle()}
      />
    </div>
  );
};

export default NewHomePage;
